import { Alert, Box, Button, Typography, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface TransactionLimitAlertProps {
    onNavigateToPlans: () => void;
    transactionCount: number;
    planLimit: number;
}

const TransactionLimitAlert: React.FC<TransactionLimitAlertProps> = ({ 
    onNavigateToPlans, 
    transactionCount,
    planLimit 
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    const countOverLimit = Math.max(0, Math.abs(transactionCount - planLimit));
    const hasNoTransactions = transactionCount === 0;

    // Dark mode uses a darker background with bright text
    // Light mode uses a light blue background with dark blue text
    const styles = {
        backgroundColor: isDarkMode ? '#1e3a5f' : '#e3f2fd',
        textPrimary: isDarkMode ? '#ffffff' : '#1565c0',
        textSecondary: isDarkMode ? '#bbdefb' : '#1976d2',
        border: isDarkMode ? '#2196f3' : '#bbdefb',
        buttonBg: '#2196f3',
        buttonHover: '#1976d2',
        iconColor: isDarkMode ? '#90caf9' : '#2196f3'
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Alert
                severity="info"
                icon={<InfoOutlinedIcon sx={{ fontSize: '1.5rem', color: styles.iconColor }} />}
                sx={{
                    backgroundColor: styles.backgroundColor,
                    color: styles.textPrimary,
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    border: `1px solid ${styles.border}`,
                    borderRadius: 1.5,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: hasNoTransactions ? 'flex-start' : 'space-between',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
                action={
                    !hasNoTransactions ? (
                        <Button 
                            variant="contained" 
                            onClick={onNavigateToPlans}
                            sx={{
                                backgroundColor: styles.buttonBg,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                textTransform: 'none',
                                px: 2,
                                py: 1,
                                '&:hover': {
                                    backgroundColor: styles.buttonHover
                                }
                            }}
                        >
                            {t('taxReports.buttons.goToPlans')}
                        </Button>
                    ) : null
                }
            >
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    width: '100%',
                    alignItems: hasNoTransactions ? 'flex-start' : 'flex-start'
                }}>
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            fontWeight: 'bold', 
                            fontSize: '1.1rem',
                            mb: hasNoTransactions ? 0 : 0.5,
                            color: styles.textPrimary
                        }}
                    >
                        {hasNoTransactions 
                            ? t('taxReports.noTransactionsDetected') 
                            : t('taxReports.errors.transactionLimitExceededWithCount', { count: countOverLimit })}
                    </Typography>
                    {!hasNoTransactions && (
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontSize: '1rem',
                                color: styles.textSecondary
                            }}
                        >
                            {t('taxReports.upgradeToUnlockTransactions')}
                        </Typography>
                    )}
                </Box>
            </Alert>
        </Box>
    );
};

export default TransactionLimitAlert;
